import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
    minHeight: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha("#fff", 0.8),
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08)}`,
    borderRadius: "16px 16px 0 0",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(2),
    },
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `linear-gradient(135deg, #e91e63 0%, #c2185b 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 6px 20px ${alpha("#e91e63", 0.35)}`,
    "& svg": {
      color: "#fff",
      fontSize: 24,
    },
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.5rem",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #fff 0%, #ccc 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  titleCount: {
    background: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    fontSize: "0.85rem",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  actionsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchField: {
    minWidth: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.default, 0.5)
        : "#fff",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  addButton: {
    borderRadius: 12,
    padding: theme.spacing(1.2, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    background: `linear-gradient(135deg, #e91e63 0%, #c2185b 100%)`,
    boxShadow: `0 4px 14px ${alpha("#e91e63", 0.35)}`,
    "&:hover": {
      background: `linear-gradient(135deg, #c2185b 0%, #ad1457 100%)`,
      boxShadow: `0 6px 20px ${alpha("#e91e63", 0.45)}`,
      transform: "translateY(-1px)",
    },
  },
  mainPaper: {
    flex: 1,
    margin: theme.spacing(0, 2, 2, 2),
    borderRadius: 16,
    overflow: "hidden",
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.05)}`,
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(0, 0, 0, 0.08)",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.default, 0.5)
      : alpha(theme.palette.grey[100], 0.8),
    "& th": {
      fontWeight: 700,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      padding: theme.spacing(2),
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    transition: "all 0.2s ease",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.primary.main, 0.04),
    },
    "& td": {
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.06) : alpha("#000", 0.06)}`,
    },
  },
  cellText: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  priorityChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 8,
    height: 28,
  },
  priorityHigh: {
    background: alpha(theme.palette.error.main, 0.15),
    color: theme.palette.error.main,
  },
  priorityMedium: {
    background: alpha(theme.palette.warning.main, 0.15),
    color: theme.palette.warning.dark,
  },
  priorityLow: {
    background: alpha(theme.palette.info.main, 0.15),
    color: theme.palette.info.main,
  },
  statusActive: {
    background: alpha(theme.palette.success.main, 0.15),
    color: theme.palette.success.main,
  },
  statusInactive: {
    background: alpha(theme.palette.grey[500], 0.15),
    color: theme.palette.grey[600],
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    color: theme.palette.text.secondary,
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  editButton: {
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.15),
      color: theme.palette.primary.main,
    },
  },
  deleteButton: {
    "&:hover": {
      background: alpha(theme.palette.error.main, 0.15),
      color: theme.palette.error.main,
    },
  },
}));

const Announcements = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  // trava para nao acessar pagina que não pode  
  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error(i18n.t("announcements.toasts.info"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-announcement`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
      await api.delete(`/announcements/${announcement.id}/media-upload`);

      await api.delete(`/announcements/${announcement.id}`);
      
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const translatePriority = (val) => {
    if (val === 1) {
      return i18n.t("announcements.high");
    }
    if (val === 2) {
      return i18n.t("announcements.medium");
    }
    if (val === 3) {
      return i18n.t("announcements.low");
    }
  };

  const getPriorityClass = (val) => {
    if (val === 1) return classes.priorityHigh;
    if (val === 2) return classes.priorityMedium;
    return classes.priorityLow;
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ConfirmationModal
        title={
          deletingAnnouncement &&
          `${i18n.t("announcements.confirmationModal.deleteTitle")} ${deletingAnnouncement.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />
      
      {/* Modern Header */}
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Box className={classes.titleIcon}>
            <AnnouncementIcon />
          </Box>
          <Typography className={classes.titleText}>
            {i18n.t("announcements.title")}
          </Typography>
          <span className={classes.titleCount}>{announcements.length}</span>
        </Box>
        <Box className={classes.actionsWrapper}>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t("announcements.searchPlaceholder")}
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleOpenAnnouncementModal}
            startIcon={<AddIcon />}
          >
            {i18n.t("announcements.buttons.add")}
          </Button>
        </Box>
      </Box>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <Table size="small" className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="left">
                {i18n.t("announcements.table.title")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.priority")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.mediaName")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {announcements.map((announcement) => (
                <TableRow key={announcement.id} className={classes.tableRow}>
                  <TableCell align="left">
                    <Typography className={classes.cellText}>
                      {announcement.title}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={translatePriority(announcement.priority)}
                      size="small"
                      className={`${classes.priorityChip} ${getPriorityClass(announcement.priority)}`}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.cellText} style={{ fontSize: "0.85rem" }}>
                      {announcement.mediaName ?? i18n.t("quickMessages.noAttachment")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={announcement.status ? i18n.t("announcements.active") : i18n.t("announcements.inactive")}
                      size="small"
                      className={`${classes.priorityChip} ${announcement.status ? classes.statusActive : classes.statusInactive}`}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEditAnnouncement(announcement)}
                          className={`${classes.actionButton} ${classes.editButton}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmModalOpen(true);
                            setDeletingAnnouncement(announcement);
                          }}
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  )
};

export default Announcements;
